/**
 * MultiMeasurementGrid Component
 *
 * Renders a table of measurement inputs for items with sub-items and/or multiple measurements.
 * Rows = sub-items (positions), Columns = measurements.
 * Each cell is a MeasurementInput.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import { MeasurementInput } from './MeasurementInput';
import type {
  MeasurementDefinition,
  SubItemDefinition,
  MeasurementValueEntry,
} from '@/types';

interface MultiMeasurementGridProps {
  subItems: SubItemDefinition[];
  measurements: MeasurementDefinition[];
  values: MeasurementValueEntry[];
  onChange?: (values: MeasurementValueEntry[]) => void;
  disabled?: boolean;
}

export function MultiMeasurementGrid({
  subItems,
  measurements,
  values,
  onChange,
  disabled = false,
}: MultiMeasurementGridProps) {
  const { t } = useTranslation('inspection');

  const getValue = useCallback(
    (subItemId: string | undefined, measurementId: string): number | null => {
      const entry = values.find(
        (v) => v.measurementId === measurementId && v.subItemId === subItemId
      );
      return entry?.value ?? null;
    },
    [values]
  );

  const handleCellChange = useCallback(
    (subItemId: string | undefined, measurementId: string, value: number | null) => {
      if (!onChange) return;

      const newValues = values.filter(
        (v) => !(v.measurementId === measurementId && v.subItemId === subItemId)
      );

      if (value !== null) {
        newValues.push({ subItemId, measurementId, value });
      }

      onChange(newValues);
    },
    [values, onChange]
  );

  const sortedSubItems = [...subItems].sort((a, b) => a.sortOrder - b.sortOrder);
  const sortedMeasurements = [...measurements].sort((a, b) => a.sortOrder - b.sortOrder);

  // If no sub-items, render measurements as a simple row
  if (subItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {sortedMeasurements.map((measurement) => (
          <Box key={measurement.id} sx={{ flex: '1 1 200px', minWidth: 150 }}>
            <MeasurementInput
              value={getValue(undefined, measurement.id)}
              unit={measurement.unit}
              onChange={
                !disabled && onChange
                  ? (v) => handleCellChange(undefined, measurement.id, v)
                  : undefined
              }
              disabled={disabled}
              min={measurement.min}
              max={measurement.max}
              warningMin={measurement.warningMin}
              label={measurement.label}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 140 }}>
              {t('item.position')}
            </TableCell>
            {sortedMeasurements.map((measurement) => (
              <TableCell key={measurement.id} align="center" sx={{ fontWeight: 600 }}>
                <Typography variant="caption" fontWeight={600}>
                  {measurement.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  ({measurement.unit})
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSubItems.map((subItem) => (
            <TableRow key={subItem.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {subItem.label}
                </Typography>
              </TableCell>
              {sortedMeasurements.map((measurement) => (
                <TableCell key={measurement.id} align="center" sx={{ p: 0.5 }}>
                  <MeasurementInput
                    value={getValue(subItem.id, measurement.id)}
                    unit={measurement.unit}
                    onChange={
                      !disabled && onChange
                        ? (v) => handleCellChange(subItem.id, measurement.id, v)
                        : undefined
                    }
                    disabled={disabled}
                    min={measurement.min}
                    max={measurement.max}
                    warningMin={measurement.warningMin}
                    showProgress={false}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
